import React from 'react';
import { injectIntl, type WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';

//@ts-ignore
import { CLESSON_MODES } from '@yandex-int/k-common/constants';
import {
    showIntlMessageNotification as showIntlMessageNotificationAction,
    showNetworkError as showNetworkErrorAction,
} from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { type InjectedProps as InjectedRouterPathProps, withRouterPath, withSocketIO } from 'platform-components/hocs';
import { Action, bindActionCreators, Dispatch } from 'redux';

import * as classSelectors from 'common.components/classes/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonAssignmentActions from 'common.components/lesson-view.assignment/actions';
import * as lessonAssignmentSelectors from 'common.components/lesson-view.assignment/selectors';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import * as userActions from 'common.components/user/actions';
import { getCourseByClassAndId, getDefaultCourseId } from 'store/courses/selectors';
import * as externalLmsSelectors from 'store/external-lms/selectors';
import { getMainPageDataByGrade } from 'store/main-page/selectors';
import { prepLessonsActions } from 'store/prep-lessons/actions';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import { actions as sidePanelActions } from '../../../actions';

import * as selectors from '../../selectors';

interface InjectedSocketIOProps {
    io: (namespace: string) => SocketIOClient.Socket;
}

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface HOCProps extends InjectedIntlProps, InjectedRouterPathProps, InjectedSocketIOProps {}

export interface Props extends StoreProps, DispatchProps, HOCProps {}

const mapStateToProps = (storeState: any, ownProps: HOCProps) => {
    const {
        location: { search },
        match: {
            params: { classId },
        },
    } = ownProps;
    const students = classSelectors.getStudents(storeState, { classId });
    const lesson = lessonSelectors.getLesson(storeState)!;
    const courseId = lessonSelectors.getLessonOrCurrentYearCourseId(storeState);
    const course = getCourseByClassAndId(storeState, { classId, courseId });
    const lessonId = lesson?.id || null;
    const subject = ownProps.match?.params?.subject || lesson?.subject || course?.subject;
    const currentClass = classSelectors.getClassById(storeState, { classId });

    return {
        assignDate: lessonAssignmentSelectors.getAssignDate(storeState),
        duration: lessonAssignmentSelectors.getDuration(storeState),
        assignmentState: lessonAssignmentSelectors.getAssignmentState(storeState),
        assignmentTable: Object.keys(lessonSelectors.getAssignmentTable(storeState)),
        buttonMode: selectors.getActionButtonMode(storeState, { courseId, lessonId, search }),
        canAssignLesson: selectors.getCanAssignLesson(storeState),
        lesson,
        courseId,
        deadlineDate: lessonAssignmentSelectors.getDeadlineDate(storeState),
        isEvaluable: lessonSelectors.getIsLessonEvaluable(storeState),
        lessonMode: lessonAssignmentSelectors.getLessonMode(storeState) || CLESSON_MODES.LESSON,
        loadingStatus: lessonSelectors.getLessonAssignmentLoadingStatus(storeState),
        isLoadingLessonViewData: lessonViewSelectors.getIsLoadingLessonViewData(storeState, {
            courseId,
            lessonsIds: lessonId ? [lessonId] : [],
        }),
        selectedStudents: lessonAssignmentSelectors.getSelectedStudents(storeState),
        shouldGetAssignmentDatesFromMeta: lessonSelectors.getShouldGetAssignmentDatesFromMeta(storeState),
        shouldShowSubscriptionModal: lessonViewSelectors.getShouldShowSubscriptionModal(storeState, {
            courseId,
        }),
        me: getMe(storeState),
        students,
        mesBackUrl: externalLmsSelectors.getMesHomeworkBackUrl(storeState, { search }),
        isTeacherFromMes: getIsTeacherFromMes(search),
        shouldAssignInExternalLms: lessonViewSelectors.getShouldAssignInExternalLms(storeState),
        studentsTable: selectors.getStudentsTable(storeState, { classId }),
        defaultCourseId: getDefaultCourseId(storeState, { classId, subject }),
        subject,
        currentClass,
        mainPageData: getMainPageDataByGrade(storeState, { grade: currentClass.number })?.page,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    return bindActionCreators(
        {
            clearEditor: lessonActions.clearEditor,
            setDeadlineDateError: lessonAssignmentActions.setDeadlineDateError,
            addStudentsToLesson: lessonViewActions.addStudentsToLesson,
            assignLesson: lessonViewActions.assignLesson,
            getClessonWithResultsRequest: lessonActions.getClessonWithResultsRequest,
            setWizardVariables: userActions.setWizardVariables,
            showNetworkError: showNetworkErrorAction,
            toggleLessonModal: lessonViewActions.toggleModal,
            setAssignmentState: lessonAssignmentActions.setAssignmentState,
            setExpanded: sidePanelActions.setExpanded,
            showIntlMessageNotification: showIntlMessageNotificationAction,
            startTeamBuilder: teamBuilderActions.start,
            clearPrepLessonsDetails: prepLessonsActions.clearPrepLessonsDetails,
        },
        dispatch
    );
};

export const AssignLessonButtonWrapper = (Component: React.ComponentType<Props>) =>
    injectIntl(withSocketIO(withRouterPath(connect(mapStateToProps, mapDispatchToProps)(Component))));

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mixin rule
export type TBaseConstructor = new (...args: any[]) => React.Component<Props>;
